import type { Agent, GeneralData } from './auth.schema';

/**
 * Reading the agent record off the director's own response.
 *
 * The portal is white-labelled — it is served by whichever registered agent
 * holds the director's companies — so these answer "whose portal is this" and
 * "what do they let a director pay with".
 *
 * A director can hold companies under more than one agent, which is why the
 * two questions have different answers. Branding has to pick one, so it takes
 * the first. The payment flags are searched across all of them, because the
 * legacy portal did exactly that: a flag set by any agent applies, and reading
 * only `agents[0]` would silently charge a merchant fee the second agent had
 * agreed to absorb.
 */

/** The agent whose brand the portal wears. */
export const brandingAgentOf = (general: GeneralData | undefined): Agent | undefined =>
  general?.agents[0];

/** The agent's logo, or nothing when they never uploaded one. Callers fall back
 *  to the portal's own mark rather than rendering a broken image. */
export const agentLogoOf = (general: GeneralData | undefined): string | undefined => {
  const logo = brandingAgentOf(general)?.logoURL;
  return logo === null || logo === undefined || logo.trim() === '' ? undefined : logo;
};

/** True when an agent absorbs the card merchant fee, so the director is not
 *  charged it. Any agent setting it is enough. */
export const agentPaysMerchantFee = (general: GeneralData | undefined): boolean =>
  general?.agents.some((agent) => agent.agentToPayMerchantFee) ?? false;

/** True when card payment is closed off for SMSF companies. */
export const isSmsfCardDisabled = (general: GeneralData | undefined): boolean =>
  general?.agents.some((agent) => agent.disableSmsfCC) ?? false;
